import { useRef, useState } from "react";
import styled from "styled-components";
import { format, startOfWeek, endOfWeek } from "date-fns";
import { BaseButton, Divider, Row, SizedBox } from "../CommonBaseComponents";
import CustomIcon from "../image/CustomIcon";
import { C4Text } from "../text/CustomText";
import { useClickOutside } from "../../hooks/useClickOutside";
import CalendarComponent from "../calendar/CalendarComponent";
import SecondaryButton from "./SecondaryButton";
import PrimaryButton from "./PrimaryButton";
import { Theme } from "../../Theme";

// day/week/month/year 세그먼트와 동일한 배경 컨테이너
const Container = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 6px;
    border-radius: 8px;
    background-color: ${Theme.color.neutral100};
`;

// 버튼 + 팝오버를 함께 감싸 캘린더를 버튼 바로 아래에 위치시키는 앵커
const ButtonAnchor = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

// 클릭한 버튼 아래, 오른쪽 정렬로 펼쳐지는 캘린더 팝오버
const CalendarPopover = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 1000;
    margin-top: 8px;
    width: 340px;
    background-color: ${Theme.color.white_07};
    border-radius: 12px;
    backdrop-filter: blur(30px);
    -webkit-backdrop-filter: blur(30px);
    box-shadow: 0 3px 16px ${Theme.color.black_01};
    padding: 16px;
    box-sizing: border-box;

    /* week: 선택 주 전체 — 연보라 배경 */
    .rdp-week-highlight {
        background-color: #6250fc1a;
    }
    .rdp-week-highlight .rdp-day_button {
        color: ${Theme.color.indigo500};
    }
    /* 기준일(day / week 의 일·토)은 보라 원형 + 흰 텍스트 (week 배경 위에서도 유지) */
    .rdp-selected .rdp-day_button {
        background-color: ${Theme.color.indigo500};
        color: ${Theme.color.white};
        border-radius: 100%;
    }
    /* day: 시작↔종료 연결 — 사이 날짜 연보라 배경 */
    .rdp-range-mid {
        background-color: #6250fc1a;
    }
    .rdp-range-mid .rdp-day_button {
        color: ${Theme.color.indigo500};
    }
    /* day: 반대편 확정일(anchor)도 보라 원형 + 흰 텍스트 */
    .rdp-range-anchor .rdp-day_button {
        background-color: ${Theme.color.indigo500};
        color: ${Theme.color.white};
        border-radius: 100%;
    }
    /* day: 끝점 셀의 연결 방향 절반을 연보라로 채워 사이 기간과 이어지게 (원형은 그대로 위에) */
    .rdp-range-fill-right {
        background: linear-gradient(to right, transparent 50%, #6250fc1a 50%);
    }
    .rdp-range-fill-left {
        background: linear-gradient(to left, transparent 50%, #6250fc1a 50%);
    }
`;

// periodType -> 캘린더 뷰 타입
const viewByPeriod = { day: "day", week: "day", month: "month", year: "year" };

// 항상 YYYY-MM-DD 자리를 차지해 버튼 폭을 day 길이로 고정 (숫자 폭 일정하게 tabular-nums)
const DateText = styled.span`
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

// month -> -DD, year -> -MM-DD 구간을 숨기되 폭은 유지
const HideableSegment = styled.span`
    visibility: ${({ $hidden }) => ($hidden ? "hidden" : "visible")};
`;

// 버튼에 표시할 날짜 계산
//  - day  : 선택일
//  - week : 시작 버튼 = 시작 주의 첫 날(일), 종료 버튼 = 마지막 주의 끝 날(토)
//  - month/year : 선택값 (표시는 DD / MM-DD 숨김)
function resolveDate(value, periodType, which) {
    if (!value) return null;
    if (periodType === "week") {
        return which === "from" ? startOfWeek(value) : endOfWeek(value);
    }
    return value;
}

function DateButton({ date, placeholder, periodType, active, onClick }) {
    const color = active ? Theme.color.indigo500 : Theme.color.zinc850;
    let label;
    if (date) {
        const [year, month, day] = format(date, "yyyy-MM-dd").split("-");
        label = (
            <DateText>
                {year}
                <HideableSegment $hidden={periodType === "year"}>{`-${month}`}</HideableSegment>
                <HideableSegment $hidden={periodType === "year" || periodType === "month"}>{`-${day}`}</HideableSegment>
            </DateText>
        );
    } else {
        label = placeholder;
    }

    return (
        <BaseButton
            padding={"6px 10px"}
            border={active ? `1px solid ${Theme.color.indigo500}` : `1px solid ${Theme.color.gray200}`}
            borderRadius={"8px"}
            backgroundColor={Theme.color.white}
            hoverBackgroundColor={Theme.color.zinc100}
            onClick={onClick}
        >
            <Row alignItem={"center"} cursor={"pointer"}>
                <CustomIcon iconName={active ? "date_active" : "date"} width={"13.5px"} height={"12px"} />
                <SizedBox width={"7px"} />
                <C4Text fontColor={color}>{label}</C4Text>
                <SizedBox width={"6px"} />
                <CustomIcon iconName={"chevron_down"} isActive={active} size={"16px"} cursor={"pointer"} />
            </Row>
        </BaseButton>
    );
}

/**
 * 시작일/종료일 버튼. 버튼당 하나의 값을 단일 선택한다.
 * periodType 에 따라 캘린더 뷰가 달라진다.
 *  - day  : 하루 단일 선택
 *  - week : 한 날 클릭 시 해당 주(일~토) 연보라 하이라이트
 *  - month: 월 단일 선택
 *  - year : 연도 단일 선택
 * 캘린더는 클릭한 버튼 바로 아래(오른쪽 정렬)에 노출된다.
 * @param {{from?: Date, to?: Date}} range 선택된 기간
 * @param {(range: {from?: Date, to?: Date}) => void} setRange 적용 콜백
 * @param {"day"|"week"|"month"|"year"} periodType 캘린더 뷰 타입 결정
 */
function CustomDateRangeButton({ range, setRange, periodType = "day" }) {
    const [isOpen, setIsOpen] = useState(false);
    const [focused, setFocused] = useState(null); // "from" | "to"
    const [draft, setDraft] = useState(null); // 현재 편집 중인 Date

    const view = viewByPeriod[periodType] ?? "day";

    const menuRef = useRef(null);
    useClickOutside(menuRef, () => {
        setIsOpen(false);
        setFocused(null);
    });

    const openCalendar = (which) => {
        setDraft(range?.[which] ?? null);
        setFocused(which);
        setIsOpen(true);
    };

    const closeCalendar = () => {
        setIsOpen(false);
        setFocused(null);
    };

    const commit = (value) => {
        const other = range?.[focused === "from" ? "to" : "from"];
        if (other) {
            // week: 두 주의 순서를 자동 정렬 (이전 주를 고르면 시작/종료 자동 교체)
            if (periodType === "week") {
                const w1 = startOfWeek(value);
                const w2 = startOfWeek(other);
                const [earlier, later] = w1 < w2 ? [w1, w2] : [w2, w1];
                setRange({ from: startOfWeek(earlier), to: endOfWeek(later) });
                closeCalendar();
                return;
            }
            // month/year: 선택값 순서를 자동 정렬 (이전을 고르면 시작/종료 자동 교체)
            if (periodType === "month" || periodType === "year") {
                const [earlier, later] = value < other ? [value, other] : [other, value];
                setRange({ from: earlier, to: later });
                closeCalendar();
                return;
            }
        }
        setRange({ ...range, [focused]: value });
        closeCalendar();
    };

    // day/week: 캘린더에서 선택 후 Apply 로 확정
    const applyValue = () => {
        if (draft) {
            commit(draft);
        } else {
            closeCalendar();
        }
    };

    // day 모드: 시작↔종료 연결 표시 + 반대편 확정일 비활성 범위
    //  - 종료(to) 편집: anchor=시작일, 시작일 이전 비활성
    //  - 시작(from) 편집: anchor=종료일, 종료일 이후 비활성 (범위 유효성 유지)
    const isDay = periodType === "day";
    const rangeAnchor = isDay ? (focused === "to" ? range?.from : range?.to) : null;
    let disabledMatcher;
    if (isDay && focused === "to" && range?.from) {
        disabledMatcher = { before: range.from };
    } else if (isDay && focused === "from" && range?.to) {
        disabledMatcher = { after: range.to };
    }

    const calendarContent = (
        <CalendarPopover>
            <CalendarComponent
                type={view}
                rangeAnchor={rangeAnchor}
                disabledMatcher={disabledMatcher}
                // day / week 뷰. week 는 시작 버튼=그 주의 첫날(일), 종료 버튼=마지막날(토)로 저장 + 주 배경 강조
                mode={periodType === "week" ? "week" : "single"}
                selectedDate={draft}
                onSelectDate={(day) =>
                    setDraft(
                        periodType === "week" ? (focused === "from" ? startOfWeek(day) : endOfWeek(day)) : day
                    )
                }
                // month 뷰 (월 단일 선택)
                selectedMonth={draft}
                onMonthSelect={(date) => setDraft(date)}
                // year 뷰 (연도 단일 선택)
                selectedYear={draft?.getFullYear()}
                onYearSelect={(year) => setDraft(new Date(year, 0, 1))}
                setType={() => {}}
                onCaptionClick={() => {}}
            />
            <Divider marginTop={"12px"} />
            <Row justifyContent={"flex-end"} padding={"12px"}>
                <SecondaryButton text={"Close"} onClick={closeCalendar} padding={"7.75px 10px"} width={"70px"} />
                <SizedBox width={"7px"} />
                <PrimaryButton text={"Apply"} onClick={applyValue} padding={"7.75px 10px"} width={"70px"} />
            </Row>
        </CalendarPopover>
    );

    return (
        <Container ref={menuRef}>
            <ButtonAnchor>
                <DateButton
                    date={resolveDate(range?.from, periodType, "from")}
                    placeholder={"Start Date"}
                    periodType={periodType}
                    active={isOpen && focused === "from"}
                    onClick={() => openCalendar("from")}
                />
                {isOpen && focused === "from" && calendarContent}
            </ButtonAnchor>
            <C4Text fontColor={Theme.color.gray400}>-</C4Text>
            <ButtonAnchor>
                <DateButton
                    date={resolveDate(range?.to, periodType, "to")}
                    placeholder={"End Date"}
                    periodType={periodType}
                    active={isOpen && focused === "to"}
                    onClick={() => openCalendar("to")}
                />
                {isOpen && focused === "to" && calendarContent}
            </ButtonAnchor>
        </Container>
    );
}

export default CustomDateRangeButton;
